import ProductPageVariants from '../ProductPageVariants';
import BuyButton from '@components/BuyButton';
import React, { useState, useMemo } from 'react';
import {
  ArchievedProduct,
  Available,
  BestBeforeDate,
  BoughtAmount,
  Delivery,
  DigitalProductDeliveryType,
  IsVesovoi,
  PromocodeGiftItem,
  SubscribeProductBlock,
  Supplier,
  Title,
  TotalRating,
  TotalReviewsCount
} from '../components';
import { observer } from 'mobx-react';
import ProductPageDesktopPrice from '../ProductPageDesktopPrice';
import icons from '@icons';
import type { ProductInfoI, TaskItem } from '@routes/ProductPage/index.type';
import { twcls } from '@utils/twcls';
import ProductTask from '../ProductTask';
import { Picture } from '@ui';
import ModalMapfunc from '@components/Layout/Header/components/InfoHeader/components/ModalMapfunc';
import './index.scss';
import { useStores } from '@root/src/stores';

const {
  store: { black: storeBlackOutlined }
} = icons.Fill;

interface ProductPageMainBlockProps {
  promocodeGiftItems: NonNullable<ProductInfoI>['promocodeGiftItems'];
  id: any;
  isBarMenu: boolean;
  canBeBought: boolean;
  bestBeforeDate: any;
  amountInCart: any;
  isProductPageDesktop?: boolean;
  isMenu: boolean;
  rating: any;
  newBadges: any;
  reviewTotalCount: any;
  variants: any;
  sizeRangeItem: any;
  title: any;
  subtitle: any;
  article: any;
  deliveryDate: any;
  isVesovoi: any;
  ifWeighting: any;
  scrollToReviews: any;
  supplier_id: any;
  salesNumberInfo: any;
  isExpressPickupAval?: boolean;
  isExpressDeliveryAval?: boolean;
  isDigital: any;
  gds_count: any;
  priceObj: any;
  isPreorder: any;
  isProductNotForSale: any;
  isProductArchieved: any;
  breadCrumbsLastItem: any;
  changeUrlToUpBreadCrumbs: any;
  available: any;
  supplyDate: any;
  multiPricePromo: any;
  analogs: any;
  task: TaskItem | null;
  availableQuantity?: Array<{
    store_id: number;
    amount: number;
  }>;
}

const ProductPageMainBlock: React.FC<ProductPageMainBlockProps> = observer(
  props => {
    const { cartStore } = useStores();
    const {
      id,
      isBarMenu,
      canBeBought,
      isProductPageDesktop,
      bestBeforeDate,
      rating,
      newBadges,
      amountInCart,
      reviewTotalCount,
      variants,
      title,
      subtitle,
      article,
      deliveryDate,
      isVesovoi,
      ifWeighting,
      scrollToReviews,
      isDigital,
      supplier_id,
      multiPricePromo,
      salesNumberInfo,
      isExpressDeliveryAval,
      isExpressPickupAval,
      gds_count,
      priceObj,
      isPreorder,
      isProductNotForSale,
      isProductArchieved,
      breadCrumbsLastItem,
      changeUrlToUpBreadCrumbs,
      available,
      supplyDate,
      promocodeGiftItems,
      analogs,
      task,
      availableQuantity
    } = props;

    const [isShowModal, setIsShowModal] = useState(false);

    const filteredStores = useMemo(() => {
      if (!availableQuantity || availableQuantity.length === 0) return [];
      return availableQuantity.filter(item => item.store_id !== 1);
    }, [availableQuantity]);

    const isProductAvailableInStores = useMemo(() => {
      if (!filteredStores || filteredStores.length === 0) return false;
      return filteredStores.some(item => item.amount > 0);
    }, [filteredStores]);

    const handleShowStoresModal = (): void => {
      if (isProductAvailableInStores) {
        setIsShowModal(true);
      }
    };

    const triggerChangePickupStore = (id: any): void => {
      cartStore.updateCartStage({ storeId: id, deliveryId: 5 });
    };
    return (
      <div className='pp-main-block'>
        {isShowModal && (
          <ModalMapfunc
            changeModalState={setIsShowModal}
            pickups={[]}
            triggerChangePickupStore={triggerChangePickupStore}
            setShowModal={setIsShowModal}
            activeStore={null}
            isHideDelivery={true}
            isShouldAutoSwitchPickupStore={false}
            isShowClusterer
            availableQuantity={availableQuantity}
            isShowAvailableQuantity={true}
          />
        )}

        {!isProductNotForSale && !isProductArchieved ? (
          <div className='pp-main-block__top'>
            <TotalRating rating={rating} />
            <TotalReviewsCount
              reviewTotalCount={reviewTotalCount}
              scrollToReviews={scrollToReviews}
            />
            {bestBeforeDate && !isDigital && (
              <BestBeforeDate
                bestBeforeDate={bestBeforeDate}
                className='ml-auto'
              />
            )}
          </div>
        ) : null}
        <Title
          title={title}
          subtitle={subtitle}
          article={article}
          badges={newBadges}
        />
        {((!isProductNotForSale && !isProductArchieved) ||
          !!analogs?.length) && (
          <>
            {(!!variants?.length || isVesovoi) && (
              <div className='flex w-full mb-5 gap-x-3'>
                {variants && variants?.length > 1 && !!id && (
                  <ProductPageVariants
                    productInfoAvailable
                    productId={id}
                    variants={variants}
                  />
                )}
                {available && isVesovoi && (
                  <div
                    className={twcls(
                      'flex-shrink-0 mt-auto ml-auto pb-2',
                      (!variants?.length || variants?.length === 1) &&
                        'ml-0 mr-auto pb-0'
                    )}
                  >
                    <IsVesovoi />
                  </div>
                )}
              </div>
            )}
            {available ? (
              <>
                {isDigital && <DigitalProductDeliveryType />}
                <div
                  className={twcls(
                    'bg-[var(--ultra-light-grey)] rounded-xl p-2  mb-6',
                    task && 'mb-3'
                  )}
                >
                  <ProductPageDesktopPrice
                    multiPricePromo={multiPricePromo}
                    amountInCart={amountInCart}
                    id={id}
                    priceObj={priceObj}
                    ifWeighting={ifWeighting}
                  />

                  <div className='pp-main-block__actions'>
                    <BuyButton
                      btnTitle={isProductPageDesktop ? 'В корзину' : 'Купить'}
                      canBeBought={canBeBought}
                      available={available}
                      isPreorder={isPreorder}
                      className='product-page'
                      productId={id}
                      isDigital={isDigital}
                    />
                    <div className='flex flex-col justify-center'>
                      {!isDigital && (
                        <div className='flex flex-col gap-y-2'>
                          <Delivery
                            isExpressDeliveryAval={!!isExpressDeliveryAval}
                            isExpressPickupAval={!!isExpressPickupAval}
                            deliveryDate={deliveryDate}
                          />
                          <div
                            className={`pp-delivery ${
                              isProductAvailableInStores ? 'cursor-pointer' : ''
                            }`}
                            onClick={handleShowStoresModal}
                          >
                            {Number(isProductAvailableInStores) > 0 ? (
                              <>
                                <Picture
                                  icon={storeBlackOutlined}
                                  height='20'
                                  alt=''
                                />
                                {isExpressDeliveryAval
                                  ? 'Самовывоз от 15 мин'
                                  : 'Наличие в магазинах'}
                              </>
                            ) : (
                              <span style={{ height: '20px' }} />
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                {task && (
                  <div className='mb-3'>
                    <ProductTask task={task} />
                  </div>
                )}
                <div className='flex items-center'>
                  <div className='flex flex-wrap items-center justify-between w-full gap-x-3 gap-y-3'>
                    <BoughtAmount
                      count={salesNumberInfo?.[0]}
                      postfix={salesNumberInfo?.[1]}
                    />
                    {!isPreorder && (
                      <Available
                        gds_count={gds_count || 0}
                        available={available}
                      />
                    )}
                    {!isBarMenu && supplier_id && (
                      <Supplier supplier_id={supplier_id} />
                    )}
                  </div>
                </div>
                {!!promocodeGiftItems?.length && (
                  <div className='mb-4 max-w-[300px] mt-5'>
                    <PromocodeGiftItem
                      promocodeGiftItems={promocodeGiftItems}
                    />
                  </div>
                )}
              </>
            ) : (
              <>
                {!isBarMenu && !isProductNotForSale && !isProductArchieved && (
                  <SubscribeProductBlock id={+id} supplyDate={supplyDate} />
                )}
                {!isBarMenu && !isProductNotForSale && !isProductArchieved && (
                  <button
                    type='button'
                    onClick={changeUrlToUpBreadCrumbs}
                    className='cursor-pointer mb-6 h-[42px] rounded-lg bg-[var(--mid-yellow)] px-10 transition-colors hover:bg-[var(--main-yellow)]'
                  >
                    Все товары раздела
                  </button>
                )}
                {!isBarMenu && !isProductNotForSale && supplier_id && (
                  <Supplier supplier_id={supplier_id} />
                )}
              </>
            )}
          </>
        )}
        {!available &&
          isBarMenu &&
          !location.pathname.includes('/bar-menu/') && (
            <div
              className={twcls(
                'bg-[var(--ultra-light-grey)] rounded-xl p-2',
                task && 'mb-3'
              )}
            >
              <ProductPageDesktopPrice
                multiPricePromo={multiPricePromo}
                amountInCart={amountInCart}
                id={id}
                priceObj={priceObj}
                ifWeighting={ifWeighting}
              />
              {!!isProductAvailableInStores && (
                <div
                  className={`pp-delivery ${
                    isProductAvailableInStores ? 'cursor-pointer' : ''
                  }`}
                  onClick={handleShowStoresModal}
                >
                  <Picture icon={storeBlackOutlined} height='20' alt='' />
                  {Number(isProductAvailableInStores) > 0 &&
                    `Наличие в магазинах`}
                </div>
              )}
            </div>
          )}
        {isProductArchieved && (
          <ArchievedProduct
            breadCrumbsLastItem={breadCrumbsLastItem}
            changeUrlToUpBreadCrumbs={changeUrlToUpBreadCrumbs}
          />
        )}
      </div>
    );
  }
);

export default ProductPageMainBlock;
